/**
 * RevokeDeploymentTokenModal Component
 *
 * Modal for confirming deployment token revocation.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useRevokeDeploymentToken } from '../hooks/useRevokeDeploymentToken';
import type { DeploymentTokenSanitized } from '../../../types/deployment-token.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface RevokeDeploymentTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
  token: DeploymentTokenSanitized;
  onSuccess?: () => void;
}

/**
 * RevokeDeploymentTokenModal component
 */
export const RevokeDeploymentTokenModal: React.FC<RevokeDeploymentTokenModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  applicationId,
  token,
  onSuccess,
}) => {
  const revokeMutation = useRevokeDeploymentToken({
    tenantId,
    applicationId,
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = () => {
    if (!revokeMutation.isPending) {
      revokeMutation.reset();
      onClose();
    }
  };

  const handleRevoke = () => {
    revokeMutation.mutate(token.tokenId);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <span className="text-red-600">Revoke Deployment Token</span>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {revokeMutation.error && (
            <Alert variant="error">
              {getErrorMessage(revokeMutation.error)}
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This action cannot be undone!</p>
              <p className="text-sm">
                Once revoked, this token will immediately stop working and any CI/CD pipelines
                using it will fail to authenticate.
              </p>
            </div>
          </Alert>

          {/* Token Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Token Name:</span>
              <p className="text-gray-900 font-semibold">{token.tokenName}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Token Code:</span>
              <p className="text-gray-900 font-mono bg-gray-200 px-2 py-1 rounded inline-block mt-1">
                {token.tokenCode}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Token Prefix:</span>
              <p className="text-gray-900 font-mono text-sm">{token.tokenPrefix}...</p>
            </div>
            {token.lastUsedAt && (
              <div>
                <span className="text-sm font-medium text-gray-700">Last Used:</span>
                <p className="text-gray-900">
                  {new Date(token.lastUsedAt).toLocaleString()}
                </p>
              </div>
            )}
            <div>
              <span className="text-sm font-medium text-gray-700">Usage Count:</span>
              <p className="text-gray-900">{token.usageCount} deployments</p>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={revokeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRevoke}
            disabled={revokeMutation.isPending}
            loading={revokeMutation.isPending}
          >
            Revoke Token
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
